import type { IssuedCredentials } from './issued-credentials'
import { Download } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { CopyButton } from '~/components/CopyButton'
import { Button } from '~/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '~/components/ui/dialog'
import { downloadFromApi } from '~/helpers/files/download-from-api'

type IProps = {
  credentials: IssuedCredentials[]
  onClose: () => void
}

/**
 * Les identifiants que l'admin vient d'émettre (création, import,
 * réinitialisation, #271). Le mot de passe n'est stocké que haché : passé ce
 * dialogue, il ne se relit plus. D'où la copie et la fiche à imprimer ici même.
 */
function IssuedCredentialsDialog({ credentials, onClose }: IProps) {
  const { t } = useTranslation()
  const [downloading, setDownloading] = useState(false)

  const downloadSheet = async () => {
    setDownloading(true)
    try {
      // Seuls les champs de la carte partent : pas d'id de compte ni de `__typename`.
      const cards = credentials.map(({ username, password, name }) => ({ username, password, name }))
      await downloadFromApi('/credentials-sheet', 'identifiants.pdf', { cards })
    } catch {
      toast.error(t('credentials.sheet.failed'))
    } finally {
      setDownloading(false)
    }
  }

  return (
    <Dialog open onOpenChange={next => !next && onClose()}>
      <DialogContent
        className="max-h-[90vh] overflow-y-auto"
        // Un clic à côté ne ferme pas : les mots de passe ne se relisent plus. Échap et « Fermer » restent.
        onInteractOutside={event => event.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>{t('credentials.title', { count: credentials.length })}</DialogTitle>
          <DialogDescription>{t('credentials.once')}</DialogDescription>
        </DialogHeader>
        <ul className="flex flex-col gap-2">
          {credentials.map(credential => (
            <li key={credential.username} className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-md border border-border p-3 text-sm">
              <span className="min-w-0 flex-1">
                <span className="block text-xs text-muted-foreground">{credential.name || t('common.username')}</span>
                <span className="font-mono font-semibold">{credential.username}</span>
              </span>
              <span className="flex items-center gap-2">
                <span className="sr-only">{t('common.password')}</span>
                <code className="rounded bg-muted px-2 py-1 font-mono">{credential.password}</code>
                <CopyButton
                  value={credential.password}
                  label={t('credentials.copy', { username: credential.username })}
                  copiedMessage={t('credentials.copied')}
                  failedMessage={t('credentials.copyFailed')}
                />
              </span>
            </li>
          ))}
        </ul>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t('common.close')}</Button>
          <Button onClick={downloadSheet} disabled={downloading}>
            <Download className="mr-2 size-4" aria-hidden />
            {t('credentials.sheet.download')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default IssuedCredentialsDialog
